// src/components/PlBudgetSection.tsx (v2.19 — 経営PL 予算 vs 実績 6 chart 構成)
//
// Codex 相談で決めた採用構成 (旧 8 案サンドボックスから絞り込み):
//   ① PL サマリーカード     — 主要 7 metric × (今月+当年度累計)、 利益系は予算比差額表示
//   ② 経常利益 ウォーターフォール — 累計予算 → 累計実績の寄与分解 (旧 D)
//   ③ 医業収益100%構成比     — 収益 100% に対する費用 / 利益の積上げ (予算 vs 実績 2 本)
//   ④ 費用率スプレッドバー   — 各費用の対医業収益比 (予算 % vs 実績 %)
//   ⑤ 指標別 月次・累計トレンド — 指標+モードセレクタ (旧 B + C 統合)
//   ⑥ 差異ランキング + 年度末見通し — 左右 2 カラム (旧 H + E 統合)
//
// asOfMonth (例 "2026-04-01") から fiscal_year を計算し、 該当年度の予算/実績を表示。
//
// 単位: サマリーカードと損益トレンドは億円、他のグラフは百万円。
// 「YTD累計」表記禁止 → 「当年度累計」に統一。
// 経常利益の達成率% は廃止 → 予算差額と「赤字縮小」等のラベル表示

import { useMemo } from "react";
import * as Plot from "@observablehq/plot";
import type { PlResponse, PlBudgetPoint } from "@shared/api";
import type { PlFormat, PlProfit } from "../store";
import { KEIHI_LABEL, NONOP_METRIC, NONOP_NOTE } from "../lib/plBasis";
import { PlotFigure } from "./Plot";
import { buildMonthAxisMark } from "../lib/chartAxis";
import { useSort, SortTh } from "../lib/useSort";
import { usePlLedgerTarget } from "../lib/plLedgerApi";
import { findLedgerTarget } from "@shared/plLedger";
import { ymJp } from "../lib/format";

// ── 単位ユーティリティ ───────────────────────────────────────────
const TO_MILLION = 1000;            // 千円 → 百万円
const TO_OKU = 100_000;             // 千円 → 億円
const fmt = (v: number | null | undefined, digits = 0): string => {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("ja-JP", { maximumFractionDigits: digits, minimumFractionDigits: digits });
};
const fmtSign = (v: number | null | undefined, digits = 0): string => {
  if (v == null || !Number.isFinite(v)) return "—";
  const s = v >= 0 ? "+" : "";
  return s + v.toLocaleString("ja-JP", { maximumFractionDigits: digits, minimumFractionDigits: digits });
};
const fmtOku = (v: number | null | undefined): string => fmt(v == null ? v : v / TO_OKU, 2);
const fmtSignOku = (v: number | null | undefined): string => fmtSign(v == null ? v : v / TO_OKU, 2);

// ── 指標カテゴリ ─────────────────────────────────────────────────
// [2026-09-11 ユーザ指示] サマリーカードは **3 行 × 4 列 = 12 枚 固定**。
//   1 行目 収益系: 医業収益計 / 入院収入 / 外来収入 / 保険等査定減 (= 医業収益計 の内訳)
//   2 行目 利益系: 経常利益 / 医業利益 / 医業外損益 / 経費等
//   3 行目 費用系: 材料費計 / 人件費計 / 委託費計 / 設備関係費計
//   ★医業外損益 を 経費等 に畳まず独立させたので、 カードは利益の段階に依らず 12 枚で固定。
//     医業収益計 − (材料費計+人件費計+委託費計+設備関係費計+経費等) = 医業利益
//     医業利益 + 医業外損益 = 経常利益        (どちらも誤差 0 = lib/plBasis.ts)
const SUMMARY_METRICS: string[] = [
  "医業収益計", "入院収益", "外来収益", "保険等査定減",
  "経常利益", "医業利益", NONOP_METRIC, "経費等",
  "材料費計", "人件費計", "委託費計", "設備関係費計",
];
// 積み上げ / ウォーターフォール / 差異ランキングで使う主要指標。
// 末尾の利益はサイドバー「利益の段階」 で入れ替わり、 経常利益 のときだけ 医業外損益 が加わる。
const keyMetrics = (profit: PlProfit): string[] => [
  "医業収益計", "材料費計", "人件費計", "委託費計", "設備関係費計", "経費等",
  ...(profit === "経常利益" ? [NONOP_METRIC] : []),
  profit,
];
// 帳票の呼称 → 画面の呼称。 「入院収益 / 外来収益」 は (予算比較).xls 側、
// 「事務局経費 / 事務局人件費」 も (予算比較).xls 側の呼び名で、
// (前期比較).xls と注記は「入院収入 / 外来収入 / 本部経費 / 本部人件費」。
// 同じ費目が画面内で 2 名になるのを防ぐ (2026-09-11 敵対レビュー指摘)。
// 内部の metric 名は変えない (帳票と照合できなくなるため)。
const DISPLAY_NAME: Record<string, string> = {
  "入院収益":     "入院収入",
  "外来収益":     "外来収入",
  "事務局経費":   "本部経費",
  "事務局人件費": "本部人件費",
  "経費等":       KEIHI_LABEL,
};
const dispName = (m: string): string => DISPLAY_NAME[m] ?? m;

// 表示形式 (サイドバー plFormat) → 内部 mode
type PlMode = "month" | "ytd" | "r12";
function modeOf(format: PlFormat): PlMode {
  return format === "月別" ? "month" : format === "直近12ヶ月累計" ? "r12" : "ytd";
}
// xls の PL セクション構造に基づく分類 (網羅性を優先)
const REVENUE = new Set<string>([
  "医業収益計", "入院収益", "外来収益", "透析収益", "その他収益",
]);
const PROFIT  = new Set<string>([
  "経常利益", "医業利益", "付加価値",
]);
const EXPENSE = new Set<string>([
  // 各セクション計
  "材料費計", "人件費計", "委託費計", "設備関係費計", "研究費", "経費等", "経費計", "本部経費",
  // 材料費 内訳
  "医薬品費", "診療材料費", "医療消耗器具備品費", "給食用材料費",
  // 人件費 内訳
  "給料", "賞与", "退職給付費用", "法定福利費", "事務局人件費",
  // 委託費 内訳
  "検査委託費", "給食委託費", "寝具委託費", "医事委託費", "清掃委託費", "保守委託費", "その他の委託費",
  // 設備関係費 内訳
  // ★カッコは**全角**。 DB の値が「器機賃借料（リース料）」 なので半角で書くと一生当たらず、
  //   categoryOf が "other" を返して区分バッジが「—」 になる (2026-09-11 敵対レビュー指摘)。
  "減価償却費", "器機賃借料（賃借料）", "器機賃借料（リース料）", "地代家賃",
  "修繕費", "固定資産税等", "器機保守料", "器機設備保険料", "車両関係費",
  // 研究費 内訳
  "研究費", "研修費",
  // 経費 内訳
  "福利厚生費", "旅費交通費", "職員被服費", "通信費", "広告宣伝費", "消耗品費",
  "消耗器具備品費", "会議費", "水道光熱費", "保険料", "交際費", "募集費",
  "寄付金", "諸会費", "租税公課", "雑費", "事務局経費",
  // 医業外費用 (医業外損益 の構成要素。 単独で出すことはないが分類は持っておく)
  "支払利息", "雑損失",
]);
// 医業外の収益側 + その純額。 **収益符号** (増えるほど良い)。
// ★旧版は 雑収入 / 受取利息 / 保険等査定減 を EXPENSE に入れていたが、 これは向きが逆。
//   雑収入 (補助金) が増えるのは良いこと、 保険等査定減 は DB 上 **負値** で格納されており
//   「値が増える (0 に近づく) = 査定が減った = 良い」。 費用扱いだと色が反転する
//   (2026-09-11 敵対レビュー指摘。 どちらも従来は画面に出ていなかったので表面化していなかった)。
const NONOP = new Set<string>([
  "医業外損益", "雑収入", "受取利息",
]);
type Cat = "revenue" | "expense" | "profit" | "nonop" | "other";
const categoryOf = (m: string): Cat =>
  REVENUE.has(m) ? "revenue" :
  // 保険等査定減 は 医業収益計 の内数 (控除項目)。 増える = 査定が減る = 良い → 収益と同じ向き。
  m === "保険等査定減" ? "revenue" :
  PROFIT.has(m) ? "profit" :
  NONOP.has(m) ? "nonop" :
  EXPENSE.has(m) ? "expense" : "other";
// 「実績/予算 %」 が意味を持たない指標 (符号が振れる / 負値で格納されている) は差額で見せる。
const isDeltaMetric = (m: string, cat: Cat): boolean =>
  cat === "profit" || cat === "nonop" || m === "保険等査定減";

// 達成率の good/warn/bad (収益/利益: 100% 以上が良、 費用: 100% 以下が良)
// ※ 経常利益は達成率% を使わないので、 この関数は収益/費用に限定
function levelOf(rate: number, cat: Cat): "good" | "warn" | "bad" {
  if (cat === "expense") {
    if (rate <= 100) return "good";
    if (rate <= 105) return "warn";
    return "bad";
  }
  if (rate >= 100) return "good";
  if (rate >= 95)  return "warn";
  return "bad";
}
// 区分バッジ (サマリーカード右上 / 差異ランキングの「区分」列で共用)
function catLabel(cat: Cat): string {
  return cat === "expense" ? "費用" : cat === "profit" ? "利益"
    : cat === "revenue" ? "収益" : cat === "nonop" ? "医業外" : "—";
}
function catBadgeColor(cat: Cat): string {
  return cat === "expense" ? "#fef3c7" : cat === "profit" ? "#fce7f3"
    : cat === "revenue" ? "#dbeafe" : cat === "nonop" ? "#e0e7ff" : "#f1f5f9";
}
const levelColor = (l: "good"|"warn"|"bad") =>
  l === "good" ? { bg: "#dcfce7", ring: "#16a34a" } :
  l === "warn" ? { bg: "#fef9c3", ring: "#ca8a04" } :
                 { bg: "#fee2e2", ring: "#dc2626" };

// ── asOfMonth → fiscal_year ─────────────────────────────────────
// 2026-04 → FY2026、 2026-03 → FY2025 (4 月開始年度)
function fiscalYearOf(asOfMonth: string): number {
  const [y, m] = asOfMonth.split("-").map(Number);
  return m >= 4 ? y : y - 1;
}
// fiscal_year に属する 12 ヶ月 (4 月始まり) を YYYY-MM の文字列で返す
function fyMonths(fy: number): string[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = ((i + 3) % 12) + 1;
    const y = i + 3 < 12 ? fy : fy + 1;
    return `${y}-${String(m).padStart(2, "0")}`;
  });
}

// PlBudgetPoint[] → (metric, ym) → value Map (fiscal_year で filter したあと)
type ValMap = Map<string, Map<string, number>>;
function buildMap(rows: PlBudgetPoint[]): ValMap {
  const m: ValMap = new Map();
  for (const r of rows) {
    const ym = r.date.slice(0, 7);
    if (!m.has(r.metric)) m.set(r.metric, new Map());
    m.get(r.metric)!.set(ym, r.value);
  }
  return m;
}

// ── 派生集計 ─────────────────────────────────────────────────────
// 当年度の各 metric について、 当月 / 当年度累計 / 年度予算 / 残月予算 / 着地見通し /
// 直近 12 ヶ月累計 (rolling 12M、 年度跨ぎ OK)
type YtdRow = {
  metric: string;
  cat: Cat;
  // 当月
  monthBudget: number;
  monthActual: number | null;   // 未来月 = null
  monthDiff:   number | null;
  // 当月の前年同月 (= 12 ヶ月前の実績、 月別表示の前年比に使う)
  monthPrevYear: number | null;
  monthYoyDiff:  number | null;
  monthYoyRate:  number | null;
  // 当年度累計
  cumBudget: number;
  cumActual: number;            // 経過月までの実績累計
  cumDiff:   number;
  cumRate:   number;            // 実績/予算 *100。 経常利益では誤読のため別ロジック
  // 前年同期間累計 (= 前年度の同じ経過月数 までの累計、 fact_pl_monthly 由来)
  prevYearCum: number | null;    // データなければ null (= 前年データ範囲外)
  yoyDiff:     number | null;    // 当年度累計 - 前年同期累計
  yoyRate:     number | null;    // 当年度累計 / 前年同期累計 * 100
  // 直近 12 ヶ月累計 (currentYm から遡って 12 ヶ月)
  r12Budget:   number | null;    // 直近 12 ヶ月の予算累計 (12 月全部に予算データあれば finite、 さもなくば null)
  r12Actual:   number | null;    // 直近 12 ヶ月の実績累計
  r12Diff:     number | null;    // r12Actual - r12Budget
  r12Rate:     number | null;    // r12Actual / r12Budget *100
  // 直近 12 ヶ月の 1 年前 (= 13〜24 ヶ月前) との前年比
  r12PrevActual: number | null;
  r12YoyDiff:    number | null;
  r12YoyRate:    number | null;
  // 年度全体
  fyBudget:  number;
  fyForecast: number;           // 累計実績 + 残月予算
  fyForecastDiff: number;       // 着地見通し - 年度予算
};
type Derived = {
  fy: number;
  months: string[];                 // 当年度 12 ym (4月-3月)
  currentYm: string;                // 対象月 ym
  elapsedIdx: number;               // 経過月数 (4月 = 1)
  bMap: ValMap;
  aMap: ValMap;
  ytd: YtdRow[];                    // 全 metric (KEY7 含む)
  r12Months: string[];              // 直近 12 ヶ月 (currentYm 含めて遡及 12 ym)
  prevYearMonths: string[];         // 前年度 12 ヶ月 (4月-3月)
  pyMap: ValMap;                    // 前年度実績 (前期比較 monthly 由来、 入院収益 alias 済)
  aAllMap: ValMap;                  // 全期間実績 (monthly + budgetActual union、 入院収益 alias 済)
  bAllMap: ValMap;                  // 全期間予算 (複数年度 union)
};

// YYYY-MM の N ヶ月前を返す (例 "2026-04" -1 → "2026-03")
function ymOffset(ym: string, deltaMonths: number): string {
  const [y, m] = ym.split("-").map(Number);
  const idx = y * 12 + (m - 1) + deltaMonths;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

function deriveFy(
  data: PlResponse,
  asOfMonth: string,
): Derived | null {
  const fy = fiscalYearOf(asOfMonth);
  const allMonths = fyMonths(fy);
  const prevYearMonths = fyMonths(fy - 1);

  // 該当 FY の rows だけ抽出
  const budgetRows = data.budgetMonthly.filter((r) => {
    const ym = r.date.slice(0, 7);
    return allMonths.includes(ym);
  });
  if (budgetRows.length === 0) return null;
  const actualRows = data.budgetActual.filter((r) => allMonths.includes(r.date.slice(0, 7)));

  const bMap = buildMap(budgetRows);
  const aMap = buildMap(actualRows);

  // 前年同期間 (FY fy-1 の 4月-3月) の実績は fact_pl_monthly = data.monthly 由来 (前期比較 xls)
  // PlBudgetPoint と shape 同じ (date, metric, value) なので buildMap が使える
  const prevYearRows = data.monthly.filter((r) => prevYearMonths.includes(r.date.slice(0, 7)));
  const pyMap = buildMap(prevYearRows);
  // ★前年同期も budgetActual (予算比較 xls の実績欄) で上書きする。
  //   下の aAllMap は「重複は budgetActual を優先」なのに、 ここだけ monthly (前期比較) の
  //   ままだと、 **同じ月が 1 画面の中で 2 つの値を持つ**。
  //   実測 (2026-09-11): 2025-03 の `その他の委託費` は 前期比較 45,424 / 予算比較 93,805 で
  //   48,381 千円 違い、 福利厚生費 13 と合わせて 医業利益・経常利益 が 48,394 千円 ズレる。
  //   FY2025 を表示すると 2025-03 が「直近12ヶ月累計」 では 126,734、「前年同期」 では 78,353 になっていた。
  //   ★原本 xls 2 冊の食い違いなのでどちらが正かは経理判断だが、 **画面の中で 2 つの値を持たせない**
  //     ことは実装で決められる。 優先順位を aAllMap と揃える (= budgetActual 勝ち)。
  for (const r of data.budgetActual) {
    const ym = r.date.slice(0, 7);
    if (!prevYearMonths.includes(ym)) continue;
    if (!pyMap.has(r.metric)) pyMap.set(r.metric, new Map());
    pyMap.get(r.metric)!.set(ym, r.value);
  }

  // 経過月数: 対象月 (asOfMonth) までの月数。 対象月に実績が無ければ、 それ以前で実績のある最終月へ寄せる。
  // [2026-09-26 ユーザ指摘] 以前は「年度内で実績のある最終月」 を無条件に採っていたため、
  //   同じ年度の中で対象月を変えてもサマリー (月別 / 年度累計 / 直近12ヶ月累計) の基準月が動かなかった。
  const asOfYm = asOfMonth.slice(0, 7);
  const actualYms = [...new Set(actualRows.map((r) => r.date.slice(0, 7)))].sort()
    .filter((ym) => ym <= asOfYm);
  const currentYm = actualYms[actualYms.length - 1] ?? asOfYm;
  let elapsedIdx = allMonths.indexOf(currentYm) + 1;
  if (elapsedIdx <= 0) elapsedIdx = 0;

  // ── 直近 12 ヶ月: currentYm を含めて遡及 12 ヶ月
  // 例: currentYm "2026-03" → ["2025-04", ..., "2026-03"] (=FY2025 全期間)
  // 例: currentYm "2026-04" → ["2025-05", ..., "2026-04"] (年度跨ぎ)
  // 予算は data.budgetMonthly (FY 全体 12 ヶ月 × 複数 fy)、 実績は budgetActual と data.monthly (前期比較) の union を使う
  const r12Months: string[] = Array.from({ length: 12 }, (_, i) => ymOffset(currentYm, -(11 - i)));
  // 全予算 map (複数年度 union)
  const bAllMap = buildMap(data.budgetMonthly);
  // 全実績 map (budgetActual + monthly の union、 重複は budgetActual を優先)
  const aAllMap = buildMap(data.monthly);
  for (const r of data.budgetActual) {
    const ym = r.date.slice(0, 7);
    if (!aAllMap.has(r.metric)) aAllMap.set(r.metric, new Map());
    aAllMap.get(r.metric)!.set(ym, r.value);
  }
  // 入院収益 (予算側名) の actual / 前年実績を 入院収入 (前期比較側名) で補完。
  // 予算比較ファイル=「入院収益」、 前期比較ファイル=「入院収入」と名前が異なるため、
  // 年度跨ぎ (r12) や前年同期で履歴が欠ける箇所を相互補完する (同じ入院収益の値)。
  const PY_ALIAS: Record<string, string> = { "入院収益": "入院収入", "外来収益": "外来収入" };
  for (const [tgt, src] of Object.entries(PY_ALIAS)) {
    const srcRow = aAllMap.get(src);
    if (srcRow) {
      if (!aAllMap.has(tgt)) aAllMap.set(tgt, new Map());
      const tgtRow = aAllMap.get(tgt)!;
      for (const [ym, v] of srcRow) if (!tgtRow.has(ym)) tgtRow.set(ym, v);
    }
    const pySrc = pyMap.get(src);
    if (pySrc && !pyMap.has(tgt)) pyMap.set(tgt, pySrc);
  }
  // 1 年前の同じ 12 ヶ月 (= r12Months それぞれを -12 ヶ月)
  const r12PrevMonths = r12Months.map((ym) => ymOffset(ym, -12));

  // 全 metric について 累計集計
  const metrics = [...new Set([...bMap.keys(), ...aMap.keys()])];
  const ytd: YtdRow[] = metrics.map((metric) => {
    const bRow = bMap.get(metric);
    const aRow = aMap.get(metric);
    const pyRow = pyMap.get(metric);
    const bAllRow = bAllMap.get(metric);
    const aAllRow = aAllMap.get(metric);
    const cat = categoryOf(metric);

    // 直近 12 ヶ月予算: 12 月分全部に予算がある場合のみ finite
    let r12Budget: number | null = 0;
    let r12BMissing = false;
    if (bAllRow) {
      for (const ym of r12Months) {
        const v = bAllRow.get(ym);
        if (v == null) { r12BMissing = true; break; }
        r12Budget += v;
      }
    } else {
      r12BMissing = true;
    }
    if (r12BMissing) r12Budget = null;
    // 直近 12 ヶ月実績
    let r12Actual: number | null = 0;
    let r12AMissing = false;
    if (aAllRow) {
      for (const ym of r12Months) {
        const v = aAllRow.get(ym);
        if (v == null) { r12AMissing = true; break; }
        r12Actual += v;
      }
    } else {
      r12AMissing = true;
    }
    if (r12AMissing) r12Actual = null;
    const r12Diff = r12Actual != null && r12Budget != null ? r12Actual - r12Budget : null;
    const r12Rate = r12Actual != null && r12Budget != null && r12Budget !== 0
      ? (r12Actual / r12Budget) * 100 : null;

    // 直近 12 ヶ月 の 1 年前 累計
    let r12PrevActual: number | null = 0;
    let r12PMissing = false;
    if (aAllRow) {
      for (const ym of r12PrevMonths) {
        const v = aAllRow.get(ym);
        if (v == null) { r12PMissing = true; break; }
        r12PrevActual += v;
      }
    } else {
      r12PMissing = true;
    }
    if (r12PMissing) r12PrevActual = null;
    const r12YoyDiff = r12Actual != null && r12PrevActual != null ? r12Actual - r12PrevActual : null;
    const r12YoyRate = r12Actual != null && r12PrevActual != null && r12PrevActual !== 0
      ? (r12Actual / r12PrevActual) * 100 : null;

    let fyBudget = 0, cumBudget = 0, cumActual = 0;
    for (let i = 0; i < allMonths.length; i++) {
      const ym = allMonths[i];
      const bv = bRow?.get(ym) ?? 0;
      const av = aRow?.get(ym);
      fyBudget += bv;
      if (i < elapsedIdx) {
        cumBudget += bv;
        if (av != null) cumActual += av;
      }
    }
    // 前年同期間累計: 同じ経過月数 (i < elapsedIdx) の FY fy-1 月を累計
    // 前年のデータが 1 月でも欠ければ null (公平な比較不能)
    let prevYearCum: number | null = 0;
    let prevYearMissing = false;
    if (pyRow && elapsedIdx > 0) {
      for (let i = 0; i < elapsedIdx; i++) {
        const pyYm = prevYearMonths[i];
        const v = pyRow.get(pyYm);
        if (v == null) { prevYearMissing = true; break; }
        prevYearCum += v;
      }
    } else {
      prevYearMissing = true;
    }
    if (prevYearMissing) prevYearCum = null;
    const yoyDiff = prevYearCum != null ? cumActual - prevYearCum : null;
    const yoyRate = prevYearCum != null && prevYearCum !== 0 ? (cumActual / prevYearCum) * 100 : null;

    const monthBudget = bRow?.get(currentYm) ?? 0;
    const monthActualRaw = aRow?.get(currentYm);
    const monthActual = monthActualRaw == null ? null : monthActualRaw;
    const monthDiff   = monthActual == null ? null : monthActual - monthBudget;
    // 当月の前年同月 (12 ヶ月前の実績)
    const monthPrevRaw = aAllRow?.get(ymOffset(currentYm, -12));
    const monthPrevYear = monthPrevRaw == null ? null : monthPrevRaw;
    const monthYoyDiff = monthActual != null && monthPrevYear != null ? monthActual - monthPrevYear : null;
    const monthYoyRate = monthActual != null && monthPrevYear != null && monthPrevYear !== 0
      ? (monthActual / monthPrevYear) * 100 : null;
    const cumDiff     = cumActual - cumBudget;
    const cumRate     = cumBudget !== 0 ? (cumActual / cumBudget) * 100 : NaN;
    const fyForecast  = cumActual + (fyBudget - cumBudget);
    return {
      metric, cat,
      monthBudget, monthActual, monthDiff,
      monthPrevYear, monthYoyDiff, monthYoyRate,
      cumBudget, cumActual, cumDiff, cumRate,
      prevYearCum, yoyDiff, yoyRate,
      r12Budget, r12Actual, r12Diff, r12Rate,
      r12PrevActual, r12YoyDiff, r12YoyRate,
      fyBudget, fyForecast, fyForecastDiff: fyForecast - fyBudget,
    };
  });

  return {
    fy, months: allMonths, currentYm, elapsedIdx, bMap, aMap, ytd, r12Months,
    prevYearMonths, pyMap, aAllMap, bAllMap,
  };
}

// 利益系 (経常利益等) のステータスラベル
function profitStatus(actual: number, budget: number): { label: string; tone: "good"|"warn"|"bad"|"neutral" } {
  const aPos = actual >= 0;
  const bPos = budget >= 0;
  if (aPos && !bPos) return { label: "黒字化", tone: "good" };
  if (!aPos && bPos) return { label: "赤字転落", tone: "bad" };
  // 同符号: 改善 / 悪化
  if (aPos) {
    // 黒字継続: 実績>予算 = 拡大、 実績<予算 = 縮小
    return actual >= budget
      ? { label: "黒字拡大", tone: "good" }
      : { label: "黒字縮小", tone: "warn" };
  }
  // 赤字継続: 実績>予算 (= 赤字額小さい) = 縮小、 実績<予算 (= 赤字額大きい) = 拡大
  return actual >= budget
    ? { label: "赤字縮小", tone: "good" }
    : { label: "赤字拡大", tone: "bad" };
}

// ──────────────────────────────────────────────────────────────────
type Props = { data: PlResponse; asOfMonth: string; format: PlFormat; profit: PlProfit };

export function PlBudgetSection({ data, asOfMonth, format, profit }: Props) {
  // hooks は早期 return より先に
  const derived = useMemo(() => deriveFy(data, asOfMonth), [data, asOfMonth]);
  const fy = fiscalYearOf(asOfMonth);
  const mode = modeOf(format);

  // 予算データが無ければセクション自体非表示 (alert で促す)
  if (data.budgetMonthly.length === 0) {
    return (
      <section className="border-t pt-4 mt-6">
        <h3 className="text-sm font-semibold mb-1">📊 予算 vs 実績 (準備中)</h3>
        <p className="text-xs text-amber-700">
          予算データが取り込まれていません。 <code>pl/input/予算比較/</code> に xls を置き、
          <code>Rscript pl/script/pl_analysis.R</code> → <code>Rscript script/50_dashboard_build/build_dashboard_db.R</code> を実行してください。
        </p>
      </section>
    );
  }
  if (!derived) {
    return (
      <section className="border-t pt-4 mt-6">
        <h3 className="text-sm font-semibold mb-1">📊 予算 vs 実績 (対象年度の予算なし)</h3>
        <p className="text-xs text-amber-700">
          対象月 {ymJp(asOfMonth)} (= FY{fy}) の予算データが無いため表示できません。
          別月を選択するか、 FY{fy} の (予算比較).xls を <code>pl/input/予算比較/</code> に追加してください
        </p>
      </section>
    );
  }

  return (
    <div className="space-y-6 pl-print">
      {/* 2026-06-15 再構成: サイドバー「表示形式」で全セクションを一元制御。
          ① サマリー → ② 月次推移 → 損益トレンド → 差異ランキング → ウォーターフォール */}
      {/* [2026-07-21] ②月次推移グリッドは ①サマリーカード内へ統合 (入院実績タブと同デザイン) */}
      <Sec1_Summary derived={derived} mode={mode} />
      <Sec4_Trend data={data} derived={derived} mode={mode} profit={profit} />
      <Sec5_VarianceRanking derived={derived} mode={mode} profit={profit} />
      <Sec3_Waterfall derived={derived} mode={mode} profit={profit} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// ① PL サマリーカード
// 主要 7 metric × (当月 + 当年度累計)、 利益系は予算比差額表示
// ───────────────────────────────────────────────────────────────
function Sec1_Summary({ derived, mode }: { derived: Derived; mode: PlMode }) {
  const cards = SUMMARY_METRICS
    .map((metric) => derived.ytd.find((y) => y.metric === metric))
    .filter((x): x is YtdRow => x != null);
  const rev = derived.ytd.find((y) => y.metric === "医業収益計") ?? null;
  // [2026-07-21] 月次推移ミニ折れ線をカード内に統合 (入院実績タブと同デザイン・x軸に月tick)
  const charts = useTrendCharts(derived, mode);
  return (
    <section>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-semibold">① サマリー — {modeLabel(mode, derived)}</h3>
        <div className="ml-auto flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1"><span style={{ width: 14, height: 2, background: "#1B9E77", display: "inline-block" }}></span>実績</span>
          <span className="flex items-center gap-1"><span style={{ width: 14, height: 0, borderTop: "2px dashed #d97706", display: "inline-block" }}></span>予算</span>
          <span className="flex items-center gap-1"><span style={{ width: 14, height: 0, borderTop: "1.5px solid #94a3b8", opacity: 0.7, display: "inline-block" }}></span>前年</span>
          <span className="text-slate-400">実績(億円) / 予算比 / 前年比 / 医業収益比 / グラフ(億円)</span>
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mb-2 leading-tight">
        色の向き: <span className="text-emerald-700 font-medium">緑=良化</span>（収益・利益は予算/前年を上回る、費用は下回る）／
        <span className="text-red-700 font-medium">赤=悪化</span>（その逆）
      </div>
      {/* [2026-09-11 ユーザ指示] 3 行 × 4 列 = 12 枚 固定。 print-4cols は A4 横印刷で
          viewport 幅に依存して 1〜2 列に落ちるのを防ぐ (styles.css @media print)。 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-2 print-4cols pl-summary-grid">
        {cards.map((c) => (
          <SummaryCard key={c.metric} c={c} rev={rev} mode={mode}
            chartOpts={charts.find((ch) => ch.metric === c.metric)?.opts ?? null} />
        ))}
      </div>
    </section>
  );
}

function SummaryCard({ c, rev, mode, chartOpts }: { c: YtdRow; rev: YtdRow | null; mode: PlMode; chartOpts: Plot.PlotOptions | null }) {
  // 「実績/予算 %」 が読めない指標 (利益・医業外損益・保険等査定減) は差額で見せる
  const isDelta = isDeltaMetric(c.metric, c.cat);
  // 黒字/赤字 の言葉は利益そのものにだけ付ける (医業外損益・保険等査定減 には付けない)
  const isProfit = c.cat === "profit";

  // 表示形式に応じて表示値を切替 (月別 / 年度累計 / 直近12ヶ月累計)
  const p = pick(c, mode);
  const actual = p.actual, budget = p.budget, diff = p.diff, rate = p.rate;
  const prevYearCum = p.prevYear, yoyDiff = p.yoyDiff, yoyRate = p.yoyRate;

  // 医業収益比 (実績/予算)
  const rp = rev ? pick(rev, mode) : null;
  const revActual = rp?.actual, revBudget = rp?.budget;
  const revRatioActual = revActual != null && revActual !== 0 && actual != null
    ? (actual / revActual) * 100 : null;
  const revRatioBudget = revBudget != null && revBudget !== 0 && budget != null
    ? (budget / revBudget) * 100 : null;

  // level (color) 判定
  let level: "good" | "warn" | "bad";
  if (isProfit) {
    if (actual == null || budget == null) { level = "warn"; }
    else {
      const st = profitStatus(actual, budget);
      level = st.tone === "good" ? "good" : st.tone === "bad" ? "bad" : "warn";
    }
  } else if (isDelta) {
    // 医業外損益 / 保険等査定減: 予算より増えていれば良い (収益符号)
    level = actual == null || budget == null ? "warn" : (actual >= budget ? "good" : "bad");
  } else if (rate == null || !Number.isFinite(rate)) {
    level = "warn";
  } else {
    level = levelOf(rate, c.cat);
  }
  const col = levelColor(level);

  return (
    <div className="border rounded p-3 bg-white"
         style={{ borderLeftColor: col.ring, borderLeftWidth: 4 }}>
      <div className="text-[11px] text-slate-500 flex items-center justify-between">
        <span className="font-semibold text-slate-700">{dispName(c.metric)}</span>
        <span className="text-[10px] text-slate-400">{catLabel(c.cat)}</span>
      </div>

      {/* 実績金額をデカく */}
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-bold text-slate-900">
          {fmtOku(actual)}
        </span>
        <span className="text-xs text-slate-500">億円</span>
      </div>

      {/* 予算比 + 予算額。 ★率が読めない指標 (利益 / 医業外損益 / 保険等査定減) は差額で出す */}
      <div className="text-xs mt-1">
        {isDelta ? (
          actual != null && budget != null ? (
            <>
              {isProfit && (
                <span className="font-semibold mr-1" style={{ color: col.ring }}>
                  {profitStatus(actual, budget).label}
                </span>
              )}
              <span className="text-slate-700">
                予算比 <span className="font-bold" style={{ color: col.ring }}>{fmtSignOku(diff)} 億円</span>
              </span>
              <span className="text-slate-500"> (予算 {fmtOku(budget)} 億円)</span>
            </>
          ) : (
            <span className="text-slate-400">予算比: データなし</span>
          )
        ) : (
          <>
            予算比 <span className="font-bold" style={{ color: col.ring }}>
              {rate != null && Number.isFinite(rate) ? `${rate.toFixed(1)}%` : "—"}
            </span>
            <span className="text-slate-500"> (予算 {fmtOku(budget)} 億円)</span>
          </>
        )}
      </div>

      {/* 前年同期比 (2026-06-15: 予算比 → 前年比 → 医業収益比 の順に変更) */}
      <div className="text-xs mt-1 text-slate-700">
        {prevYearCum == null ? (
          <span className="text-slate-400">前年比: データなし</span>
        ) : isDelta ? (
          <>
            前年比: <span className="font-bold"
                          style={{ color: signColor(yoyDiff ?? 0, "revenue") }}>
              {fmtSignOku(yoyDiff)} 億円
            </span>
            <span className="text-slate-500"> (前年 {fmtOku(prevYearCum)} 億円)</span>
          </>
        ) : (
          <>
            前年比: <span className="font-bold"
                          style={{ color: signColor((yoyRate ?? 100) - 100, c.cat === "expense" ? "expense" : "revenue") }}>
              {yoyRate != null ? `${yoyRate.toFixed(1)}%` : "—"}
            </span>
            <span className="text-slate-500"> (前年 {fmtOku(prevYearCum)} 億円)</span>
            {yoyDiff != null && (
              <span className="ml-1 font-semibold"
                    style={{ color: signColor(yoyDiff, c.cat === "expense" ? "expense" : "revenue") }}>
                ({fmtSignOku(yoyDiff)} 億円)
              </span>
            )}
          </>
        )}
      </div>

      {/* 医業収益比 (前年比の後に表示) */}
      {c.metric !== "医業収益計" && (
        <div className="text-xs mt-1 text-slate-700">
          医業収益比: <span className="font-bold">
            {revRatioActual != null ? `${revRatioActual.toFixed(1)}%` : "—"}
          </span>
          <span className="text-slate-500">
            {" "}(予算 {revRatioBudget != null ? `${revRatioBudget.toFixed(1)}%` : "—"})
          </span>
          {revRatioActual != null && revRatioBudget != null && (
            <span className="ml-1 font-semibold"
                  style={{ color: signColor((revRatioActual - revRatioBudget) * (c.cat === "expense" ? 1 : -1), "expense") }}>
              ({fmtSign(revRatioActual - revRatioBudget, 1)} pt)
            </span>
          )}
        </div>
      )}

      {/* 月次推移ミニ折れ線 (億円・実績/予算/前年・x軸=月tick) */}
      {chartOpts && (
        <div className="mt-2 -mx-1">
          <PlotFigure options={chartOpts} responsiveWidth />
        </div>
      )}
    </div>
  );
}
function signColor(diff: number, cat: Cat): string {
  const isGood = cat === "expense" ? diff <= 0 : diff >= 0;
  return isGood ? "#16a34a" : "#dc2626";
}

// YtdRow から表示形式 (mode) に応じた値を取り出す共通ピッカー
type Picked = {
  budget: number | null; actual: number | null; diff: number | null;
  rate: number | null; prevYear: number | null; yoyDiff: number | null; yoyRate: number | null;
};
function pick(y: YtdRow, mode: PlMode): Picked {
  if (mode === "month") return {
    budget: y.monthBudget, actual: y.monthActual, diff: y.monthDiff,
    rate: y.monthActual != null && y.monthBudget !== 0 ? (y.monthActual / y.monthBudget) * 100 : null,
    prevYear: y.monthPrevYear, yoyDiff: y.monthYoyDiff, yoyRate: y.monthYoyRate,
  };
  if (mode === "r12") return {
    budget: y.r12Budget, actual: y.r12Actual, diff: y.r12Diff, rate: y.r12Rate,
    prevYear: y.r12PrevActual, yoyDiff: y.r12YoyDiff, yoyRate: y.r12YoyRate,
  };
  return {
    budget: y.cumBudget, actual: y.cumActual, diff: y.cumDiff,
    rate: Number.isFinite(y.cumRate) ? y.cumRate : null,
    prevYear: y.prevYearCum, yoyDiff: y.yoyDiff, yoyRate: y.yoyRate,
  };
}
// 表示形式の期間ラベル
function modeLabel(mode: PlMode, derived: Derived): string {
  if (mode === "month") return `月別 (${ymJp(derived.currentYm)})`;
  if (mode === "r12") return `直近12ヶ月累計 (${ymJp(derived.r12Months[0])} 〜 ${ymJp(derived.r12Months[11])})`;
  return `当年度累計 (〜${ymJp(derived.currentYm)})`;
}

// ───────────────────────────────────────────────────────────────
// 月次推移ミニ折れ線 (2026-06-15 新設 → 2026-07-21 サマリーカード内へ統合)。
//   入院実績タブの月次推移と同デザイン (実績=緑実線 / 予算=橙点線 / 前年=薄灰・x軸=月tick)。
//   表示形式 (月別 / 年度累計 / 直近12ヶ月累計) に追従して各点の意味を切替。
// ───────────────────────────────────────────────────────────────
type TrendPt = { date: Date; actual: number | null; budget: number | null; prevYear: number | null };

function useTrendCharts(derived: Derived, mode: PlMode) {
  return useMemo(() => {
    // metric の月次系列 (千円→億円) を表示形式に応じて構築
    const seriesFor = (metric: string): TrendPt[] => {
      const bRow = derived.bMap.get(metric);
      const aRow = derived.aMap.get(metric);
      const pyRow = derived.pyMap.get(metric);
      const bAllRow = derived.bAllMap.get(metric);
      const aAllRow = derived.aAllMap.get(metric);
      const M = TO_OKU;
      const trail12 = (row: Map<string, number> | undefined, end: string): number | null => {
        if (!row) return null;
        let s = 0;
        for (let k = 0; k < 12; k++) { const v = row.get(ymOffset(end, -k)); if (v == null) return null; s += v; }
        return s;
      };
      const out: TrendPt[] = [];
      let bCum = 0, aCum = 0, pCum = 0; let pyOk = true;
      derived.months.forEach((ym, i) => {
        // 共通の月軸は UTC で月を読む。ローカル時刻だと JST では前月末 UTC になる。
        const date = new Date(`${ym}-01T00:00:00Z`);
        const hasActual = i < derived.elapsedIdx;
        if (mode === "month") {
          const bv = bRow?.get(ym); const av = hasActual ? aRow?.get(ym) : undefined;
          const pv = pyRow?.get(derived.prevYearMonths[i]);
          out.push({ date, budget: bv == null ? null : bv / M, actual: av == null ? null : av / M, prevYear: pv == null ? null : pv / M });
        } else if (mode === "ytd") {
          bCum += bRow?.get(ym) ?? 0;
          if (hasActual) aCum += aRow?.get(ym) ?? 0;
          const pvm = pyRow?.get(derived.prevYearMonths[i]);
          if (pvm == null) pyOk = false; else pCum += pvm;
          out.push({ date, budget: bCum / M, actual: hasActual ? aCum / M : null, prevYear: pyOk ? pCum / M : null });
        } else { // r12
          const bv = trail12(bAllRow, ym);
          const av = hasActual ? trail12(aAllRow, ym) : null;
          const pv = trail12(aAllRow, ymOffset(ym, -12));
          out.push({ date, budget: bv == null ? null : bv / M, actual: av == null ? null : av / M, prevYear: pv == null ? null : pv / M });
        }
      });
      return out;
    };
    const xDomain: [Date, Date] = [
      new Date(`${derived.months[0]}-01T00:00:00Z`),
      new Date(`${derived.months[derived.months.length - 1]}-01T00:00:00Z`),
    ];
    return SUMMARY_METRICS.map((metric) => {
      const data = seriesFor(metric);
      const rows: { date: Date; value: number; series: "実績" | "予算" | "前年" }[] = [];
      for (const d of data) {
        if (d.actual != null) rows.push({ date: d.date, value: d.actual, series: "実績" });
        if (d.budget != null) rows.push({ date: d.date, value: d.budget, series: "予算" });
        if (d.prevYear != null) rows.push({ date: d.date, value: d.prevYear, series: "前年" });
      }
      const fmtV = (v: number | null) => v == null ? "—" : `${fmt(v, 2)} 億円`;
      const opts: Plot.PlotOptions = {
        width: 460, height: 132,
        // marginBottom: 月tick (buildMonthAxisMark fontSize 11px) の分 24px
        marginLeft: 46, marginRight: 6, marginTop: 6, marginBottom: 24,
        style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "9px" },
        x: { label: null, type: "time", axis: null, domain: xDomain },
        y: { label: null, grid: true, nice: true, zero: false, tickFormat: (v: number) => fmt(v, 2) },
        marks: [
          // 2026-06-15: y=0 のルールは付けない (0始まりだと高低差が潰れるため、 zero:false で
          // データ範囲に自動ズーム)。 経常利益等の負値は nice 軸が 0 をまたいで表示する。
          Plot.lineY(rows.filter((d) => d.series === "前年"), { x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.6 }),
          Plot.dot(rows.filter((d) => d.series === "前年"), { x: "date", y: "value", fill: "#94a3b8", fillOpacity: 0.6, r: 1.6 }),
          Plot.lineY(rows.filter((d) => d.series === "予算"), { x: "date", y: "value", stroke: "#d97706", strokeWidth: 1.8, strokeDasharray: "5 3" }),
          Plot.dot(rows.filter((d) => d.series === "予算"), { x: "date", y: "value", fill: "#d97706", r: 1.6 }),
          Plot.lineY(rows.filter((d) => d.series === "実績"), { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.4 }),
          Plot.dot(rows.filter((d) => d.series === "実績"), { x: "date", y: "value", fill: "#1B9E77", r: 2.2 }),
          Plot.tip(data, Plot.pointerX({
            x: "date",
            y: (d: TrendPt) => d.actual ?? d.prevYear ?? d.budget ?? 0,
            fontSize: 11,
            title: (d: TrendPt) => [
              `${ymJp(d.date.toISOString())}`,
              `実績 : ${fmtV(d.actual)}`,
              `予算 : ${fmtV(d.budget)}`,
              `前年 : ${fmtV(d.prevYear)}`,
            ].join("\n"),
          })),
          // [2026-07-21] x軸に月tick (入院実績タブと同じ buildMonthAxisMark 流儀)
          buildMonthAxisMark(xDomain),
        ],
      };
      return { metric, opts };
    });
  }, [derived, mode]);
}

// ───────────────────────────────────────────────────────────────
// 利益 ウォーターフォール (表示形式 + 利益の段階に追従、 最終 section)
// ───────────────────────────────────────────────────────────────
function Sec3_Waterfall({ derived, mode, profit }: { derived: Derived; mode: PlMode; profit: PlProfit }) {
  const profitRow = derived.ytd.find((y) => y.metric === profit);
  // 表示形式に応じた差額 / 予算をピック
  const dOf = (metric: string): number => {
    const r = derived.ytd.find((y) => y.metric === metric);
    return r ? (pick(r, mode).diff ?? 0) : 0;
  };
  const periodWord = mode === "month" ? "" : "累計";

  const drivers: { label: string; effect: number }[] = [];
  drivers.push({ label: "医業収益計", effect: dOf("医業収益計") });
  for (const m of ["材料費計", "人件費計", "委託費計", "設備関係費計", "経費等"] as const) {
    drivers.push({ label: dispName(m), effect: -dOf(m) });
  }
  // [2026-09-11] 経常利益を選んだときだけ 医業外損益 の段を足す (収益符号のまま加算)。
  //   ★経費等 に畳まないので、 補助金の増減が「費用が減った」 に化けず、 独立の棒として見える。
  if (profit === "経常利益") {
    drivers.push({ label: NONOP_METRIC, effect: dOf(NONOP_METRIC) });
  }
  const sum = drivers.reduce((s, d) => s + d.effect, 0);
  const profitDiff = profitRow ? (pick(profitRow, mode).diff ?? 0) : 0;
  const residual = profitDiff - sum;
  // [2026-09-11] 医業収益計 − 5 費用 (+ 医業外損益) = 選んだ利益 が恒等的に成立するので
  //   残差は 0 になる。 万一 xls の構造が変わって残差が出たときだけ棒を出す (静かに丸めない)。
  if (Math.abs(residual) >= 1) drivers.push({ label: "その他 (差異)", effect: residual });

  type Step = { label: string; start: number; end: number; effect: number; kind: "start"|"+"|"−"|"end" };
  const steps: Step[] = [];
  let cum = profitRow ? (pick(profitRow, mode).budget ?? 0) : 0;
  steps.push({ label: `予算${periodWord}`, start: 0, end: cum, effect: cum, kind: "start" });
  for (const d of drivers) {
    const start = cum;
    cum += d.effect;
    steps.push({ label: d.label, start, end: cum, effect: d.effect, kind: d.effect >= 0 ? "+" : "−" });
  }
  steps.push({ label: `実績${periodWord}`, start: 0, end: cum, effect: cum, kind: "end" });

  const opts = useMemo<Plot.PlotOptions>(() => {
    const rows = steps.map((s) => ({
      label: s.label,
      lo: Math.min(s.start, s.end) / TO_MILLION,
      hi: Math.max(s.start, s.end) / TO_MILLION,
      effect: s.effect / TO_MILLION,
      kind: s.kind,
    }));
    const colorFor = (k: Step["kind"]) => k === "start" ? "#475569"
      : k === "end" ? "#0f172a"
      : k === "+" ? "#16a34a" : "#dc2626";
    return {
      width: 1100, height: 420,
      // marginBottom: 一番長いラベルは 経費等 の「経費等(経費+研修費+本部経費)」= 16 文字。
      //   11px × 16 字 ≒ 176px、 -40° 回転で縦に 176·sin40° ≒ 113px 食う → 150 で足りる
      //   (医業外損益 を畳んでいた頃は 23 文字で 190 必要だった)。
      marginLeft: 80, marginRight: 30, marginTop: 30, marginBottom: 150,
      style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" },
      x: { label: null, domain: rows.map((r) => r.label), tickRotate: -40, padding: 0.2 },
      y: { label: `百万円 (${profit}${periodWord ? " " + periodWord : ""})`, grid: true, nice: true, tickFormat: (v: number) => v.toLocaleString() },
      marks: [
        Plot.ruleY([0], { stroke: "#1f2937" }),
        Plot.barY(rows, {
          x: "label", y1: "lo", y2: "hi",
          fill: (d: typeof rows[number]) => colorFor(d.kind),
          stroke: "white", strokeWidth: 0.5,
        }),
        Plot.text(rows, {
          x: "label",
          y: (d: typeof rows[number]) => d.kind === "start" || d.kind === "end" ? d.hi : Math.max(d.hi, d.lo),
          text: (d: typeof rows[number]) =>
            d.kind === "start" || d.kind === "end"
              ? `${fmt(d.effect, 0)} 百万円`
              : `${d.effect >= 0 ? "+" : ""}${fmt(d.effect, 0)}`,
          dy: -8, fontSize: 11, fontWeight: "bold",
          fill: (d: typeof rows[number]) => colorFor(d.kind),
        }),
        Plot.tip(rows, Plot.pointer({
          x: "label", y: "hi",
          title: (d: typeof rows[number]) =>
            `${d.label}\n${d.kind === "start" || d.kind === "end" ? "累計 " : "寄与 "}${fmtSign(d.effect * TO_MILLION, 0)} 千円`,
        })),
      ],
    };
  }, [steps, profit, periodWord]);

  return (
    <section>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h3 className="text-sm font-semibold">{profit} ウォーターフォール — {modeLabel(mode, derived)} (予算 → 実績)</h3>
        <span className="text-[11px] text-slate-400"><span className="text-green-600">緑=プラス</span> / <span className="text-red-600">赤=マイナス</span></span>
      </div>
      <div className="border rounded bg-white p-3">
        <PlotFigure options={opts} responsiveWidth />
      </div>
    </section>
  );
}


// 旧 PlPanel の buildBarData / buildBarOpts ロジックを移植
//
// [2026-09-11 ユーザ裁定] **医業外損益は 経費等 に畳まず、収益側の 2 段目に立てる**。
//   収益柱 = 医業収益計 (+ 医業外損益)、 費用柱 = 5 費用 + 利益 で、 **両柱の高さが必ず一致する**:
//     医業利益 基準: 材料+人件+委託+設備+経費等+医業利益 = 医業収益計
//     経常利益 基準: 同 + 経常利益 − 医業利益 = 医業収益計 + 医業外損益
//   ★畳んでいた頃は補助金月 (26年03月 雑収入 390,524 千円) に 経費等 が −270,555 千円 になり、
//     紫が 0 の下へ伸びて 費用柱 (1,958) が 収益柱 (1,687) より高く見えていた。 これが無くなる。
//   ★医業外損益 は **収益符号のまま** 置く (プラス=収入超)。 通常月は純費用で小さく下向き
//     (雑収入 12〜20M < 支払利息 17〜21M ≒ −5M/月 = 収益柱の 0.3%)、 補助金月に大きく上向き。
const stackedExpenses = (profit: PlProfit): string[] =>
  ["材料費計", "人件費計", "委託費計", "設備関係費計", "経費等", profit];
// 収益柱に積むもの (経常利益 基準のときだけ 医業外損益 が乗る)
const stackedRevenue = (profit: PlProfit): string[] =>
  profit === "経常利益" ? ["医業収益計", NONOP_METRIC] : ["医業収益計"];
const stackDomain = (profit: PlProfit): string[] =>
  [...stackedRevenue(profit), ...stackedExpenses(profit)];
const STACK_COLOR_TREND: Record<string, string> = {
  医業収益計:   "#0a2463",
  医業外損益:   "#4b9cd3",   // 収益側の 2 段目 (医業収益計より淡い青)
  材料費計:     "#d2691e",
  人件費計:     "#dc143c",
  委託費計:     "#8b4513",
  設備関係費計: "#008080",
  経費等:       "#9b59b6",
  経常利益:     "#ff1493",
  医業利益:     "#ff1493",   // 同時に 1 本しか出ないので 経常利益 と同色でよい
};

type StackBarRow = {
  ym: string; bar: "収益" | "費用"; metric: string; label: string;
  value: number; ratio: number; showLabel: boolean;
};
type StackBarData = { rows: StackBarRow[]; income: { ym: string; value: number }[]; months: string[] };

function buildStackBarData(
  src: { date: string; metric: string; value: number }[],
  endYm: string, lastN: number, ratioThreshold: number,
  revenues: string[], expenses: string[], disp: (m: string) => string,
  startYm?: string,
): StackBarData {
  if (src.length === 0) return {
    rows: [], income: [],
    months: startYm == null ? [] : fyMonths(fiscalYearOf(endYm)).filter((ym) => ym <= endYm),
  };
  const lookup = new Map<string, number>();
  const dateSet = new Set<string>();
  for (const r of src) {
    lookup.set(`${r.date}|${r.metric}`, r.value);
    dateSet.add(r.date);
  }
  // 月別は年度の4月から、直近12ヶ月累計は選択月までの移動窓を使う。
  const dates = [...dateSet].filter((d) => {
    const ym = d.slice(0, 7);
    return ym <= endYm && (startYm == null || ym >= startYm);
  }).sort();
  const lastDates = dates.slice(-lastN);
  const incomeByYm = new Map<string, number>();
  for (const d of lastDates) {
    const inc = lookup.get(`${d}|医業収益計`) ?? 0;
    incomeByYm.set(d.slice(0, 7), inc / TO_OKU);
  }
  // 収益柱の頂点ラベル = 柱の合計 (医業収益計 + 医業外損益)。 費用柱の合計と必ず一致する。
  const income = lastDates.map((d) => ({
    ym: d.slice(0, 7),
    value: revenues.reduce((a, m) => a + (lookup.get(`${d}|${m}`) ?? 0), 0) / TO_OKU,
  }));
  const rows: StackBarRow[] = [];
  for (const d of lastDates) {
    const ym = d.slice(0, 7);
    const incMan = incomeByYm.get(ym) ?? 0;   // 比率の分母は 医業収益計 のまま
    for (const rv of revenues) {
      const v = (lookup.get(`${d}|${rv}`) ?? 0) / TO_OKU;
      const ratio = incMan > 0 ? (v / incMan) * 100 : 0;
      rows.push({ ym, bar: "収益", metric: rv, label: disp(rv), value: v, ratio,
                  showLabel: rv === "医業収益計" ? false : Math.abs(ratio) >= ratioThreshold });
    }
    for (const exp of expenses) {
      const v = (lookup.get(`${d}|${exp}`) ?? 0) / TO_OKU;
      const ratio = incMan > 0 ? (v / incMan) * 100 : 0;
      rows.push({ ym, bar: "費用", metric: exp, label: disp(exp), value: v, ratio,
                  showLabel: Math.abs(ratio) >= ratioThreshold });
    }
  }
  // 月別はデータ欠損月があっても軸の開始を4月に固定する（欠損月の棒は作らない）。
  const months = startYm == null
    ? lastDates.map((d) => d.slice(0, 7))
    : fyMonths(fiscalYearOf(endYm)).filter((ym) => ym <= endYm);
  return { rows, income, months };
}

function labelFontSizeTrend(ratio: number): number {
  // 2026-06-15: 文字が小さすぎて読めない指摘を受け全体的に拡大
  const a = Math.abs(ratio);
  if (a >= 25) return 16;
  if (a >= 10) return 14;
  if (a >= 4)  return 12;
  return 11;
}

function buildStackOpts(bar: StackBarData, yLabel: string, domain: string[], disp: (m: string) => string): Plot.PlotOptions {
  return {
    // width=1920: PlotFigure(responsiveWidth) は options.width を上限に container 幅へ縮小する
    // ので、 上限を上げて画面幅いっぱいに広げる。 height も拡大して縦に余裕を持たせる。
    width: 1920, height: 600,
    marginLeft: 64, marginRight: 64, marginTop: 34, marginBottom: 70,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "13px" },
    x: { label: null, padding: 0.18, axis: null },
    fx: { label: null, padding: 0.08, domain: bar.months, tickRotate: -45 },
    y: { label: yLabel, grid: true, nice: true, tickFormat: (v: number) => v.toLocaleString() },
    color: { domain: domain.map(disp), range: domain.map((m) => STACK_COLOR_TREND[m]), legend: true },
    marks: [
      Plot.ruleY([0], { stroke: "#1f2937", strokeWidth: 1 }),
      Plot.barY(bar.rows, { fx: "ym", x: "bar", y: "value", fill: "label", stroke: "white", strokeWidth: 0.4 }),
      // 非表示の医業収益計も積み上げ計算に含める。除くと医業外損益の文字だけ 0 から積まれる。
      Plot.text(bar.rows, Plot.stackY({
        fx: "ym", x: "bar", y: "value",
        text: (d: StackBarRow) => `${fmt(d.value, 2)}億\n${d.ratio.toFixed(1)}%`,
        fontSize: (d: StackBarRow) => labelFontSizeTrend(d.ratio),
        fontWeight: "bold", fill: "white",
        stroke: (d: StackBarRow) => STACK_COLOR_TREND[d.metric] ?? "black",
        strokeWidth: 2, paintOrder: "stroke", lineHeight: 1.0,
        fillOpacity:   (d: StackBarRow) => d.showLabel ? 1 : 0,
        strokeOpacity: (d: StackBarRow) => d.showLabel ? 1 : 0,
      } as Plot.TextOptions)),
      Plot.text(bar.income, {
        fx: "ym", x: () => "収益", y: "value",
        text: (d) => `${fmt(d.value, 2)}億`,
        dy: -10, textAnchor: "middle", fill: "#0a2463",
        fontSize: 14, fontWeight: "bold",
        stroke: "white", strokeWidth: 3, paintOrder: "stroke",
      }),
      Plot.tip(bar.rows, Plot.pointer({
        fx: "ym", x: "bar", y: "value",
        title: (d: StackBarRow) =>
          `${d.ym} ${d.label}\n${fmt(d.value, 2)} 億円` +
          (d.bar === "費用" ? ` (${d.ratio.toFixed(1)}%)` : ""),
      })),
    ],
  };
}

function Sec4_Trend({ data, derived, mode, profit }: { data: PlResponse; derived: Derived; mode: PlMode; profit: PlProfit }) {
  const domain = stackDomain(profit);
  const revenues = stackedRevenue(profit);
  const expenses = stackedExpenses(profit);
  // 当年度累計 (cum) 用の累計ポイント (各月 = 年度開始からの実績累計)
  const ytdCumPoints = useMemo(() => {
    const out: { date: string; metric: string; value: number }[] = [];
    for (const metric of stackDomain(profit)) {
      const aRow = derived.aMap.get(metric);
      let cum = 0;
      derived.months.forEach((ym, i) => {
        if (i >= derived.elapsedIdx) return;
        cum += aRow?.get(ym) ?? 0;
        out.push({ date: `${ym}-01`, metric, value: cum });
      });
    }
    return out;
  }, [derived, profit]);

  const opts = useMemo<Plot.PlotOptions>(() => {
    if (mode === "month") return buildStackOpts(buildStackBarData(data.monthly, derived.currentYm, 12, 1.5, revenues, expenses, dispName, derived.months[0]), "億円 / 月", domain, dispName);
    if (mode === "r12")   return buildStackOpts(buildStackBarData(data.rolling12, derived.currentYm, 13, 1.5, revenues, expenses, dispName), "億円 / 直近12ヶ月累計", domain, dispName);
    return buildStackOpts(buildStackBarData(ytdCumPoints, derived.currentYm, 12, 1.5, revenues, expenses, dispName), "億円 / 当年度累計", domain, dispName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.monthly, data.rolling12, ytdCumPoints, derived.currentYm, mode, profit]);

  return (
    <section>
      <h3 className="text-sm font-semibold mb-2">損益トレンド — {modeLabel(mode, derived)}</h3>
      <div className="border rounded bg-white p-3">
        <PlotFigure options={opts} responsiveWidth />
        {/* [2026-09-11] 医業外損益 を 経費等 に畳まず収益側の段にしたので、 両柱の高さは常に一致する。
            医業外損益 は通常月は純費用でわずかに下向き、 補助金が出た月に大きく上向きになる。 */}
        {profit === "経常利益" && (
          <p className="text-[11px] text-slate-500 mt-2 leading-snug">
            ※ 左の柱は 医業収益計 ＋ 医業外損益、 右の柱は 5 つの費用 ＋ 経常利益 で、 高さは必ず一致する。
            {NONOP_NOTE}
          </p>
        )}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────
// ⑤ 差異ランキング (Codex 再設計後の 5 番目、 最終 section)
// ───────────────────────────────────────────────────────────────
function Sec5_VarianceRanking({ derived, mode, profit }: { derived: Derived; mode: PlMode; profit: PlProfit }) {
  // 表示候補は 主要 7 指標 + 主要な内訳 metric。 categoryOf で区分判定済み (給料・入院収益も解決済)
  const wanted = new Set<string>([
    ...keyMetrics(profit),
    "入院収益", "外来収益", "保険等査定減",
    // ★雑収入 / 支払利息 は入れない。 医業外損益 とほぼ同じ差額で並び、 TOP7 の枠を二重に食う
    //   (実測 26年03月: 医業外損益 +370 と 雑収入 +370 が 1 位 2 位を占めた)。
    //   医業外損益 1 行 + 区分バッジ「医業外」 で足りる。
    "医薬品費", "診療材料費", "医療消耗器具備品費", "給食用材料費",
    "給料", "賞与", "退職給付費用", "法定福利費", "事務局人件費",
    "減価償却費", "器機賃借料（リース料）", "器機保守料",   // ★カッコは全角 (DB の値に合わせる)
    "経費計", "事務局経費", "水道光熱費", "募集費",
    "検査委託費", "その他の委託費",
  ]);
  type Row = { metric: string; cat: Cat; diff: number; budget: number; actual: number; isGood: boolean; pt?: number };
  const rev = derived.ytd.find((y) => y.metric === "医業収益計");
  const revP = rev ? pick(rev, mode) : null;
  const rows: Row[] = derived.ytd
    .filter((y) => wanted.has(y.metric))
    .map((y) => ({ y, p: pick(y, mode) }))
    .filter(({ p }) => p.budget != null && p.budget !== 0 && p.diff != null)
    .map(({ y, p }) => {
      // 利益/収益: 実績 > 予算 が good、 費用: 実績 < 予算 が good
      const diff = p.diff as number, budget = p.budget as number, actual = p.actual ?? 0;
      // 費用だけ「減った=良い」。 収益・利益・医業外 (雑収入 等) は「増えた=良い」。
      // ★雑収入 を費用扱いすると補助金の増加が赤く出る (2026-09-11 敵対レビュー指摘で categoryOf 是正済)。
      const isGood = y.cat === "expense" ? diff <= 0 : diff >= 0;
      let pt: number | undefined;
      if (y.cat === "expense" && revP && revP.budget && revP.actual) {
        const bPct = (budget / (revP.budget as number)) * 100;
        const aPct = (actual / (revP.actual as number)) * 100;
        pt = aPct - bPct;
      }
      return { metric: y.metric, cat: y.cat, diff, budget, actual, isGood, pt };
    });
  const good = [...rows].filter((r) => r.isGood).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff)).slice(0, 7);
  const bad  = [...rows].filter((r) => !r.isGood).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff)).slice(0, 7);
  const sGood = useSort(good);
  const sBad = useSort(bad);

  const openLedger = usePlLedgerTarget((st) => st.open);
  function RankRow({ r, good }: { r: Row; good: boolean }) {
    const color = good ? "#16a34a" : "#dc2626";
    // 2026-09-26: 元帳で内訳が引ける科目は、 名前クリックで「元帳で深掘り」 へ
    const t = findLedgerTarget(r.metric);
    return (
      <tr>
        <td className="px-2 py-1 border">
          {t ? (
            <button type="button" className="text-left text-sky-800 hover:underline"
                    title="元帳 (仕訳) で内訳を見る" onClick={() => openLedger(t.group, t.metric)}>
              {dispName(r.metric)} <span className="text-[10px]">🔎</span>
            </button>
          ) : dispName(r.metric)}
        </td>
        <td className="px-2 py-1 border text-[10px] text-center">
          <span className="px-1 py-0.5 rounded font-semibold"
                style={{ backgroundColor: catBadgeColor(r.cat) }}>{catLabel(r.cat)}</span>
        </td>
        <td className="px-2 py-1 border text-right">{fmt(r.actual / TO_MILLION)}</td>
        <td className="px-2 py-1 border text-right">{fmt(r.budget / TO_MILLION)}</td>
        <td className="px-2 py-1 border text-right font-bold" style={{ color }}>{fmtSign(r.diff / TO_MILLION)}</td>
        <td className="px-2 py-1 border text-right text-[10px]" style={{ color }}>
          {r.pt != null ? fmtSign(r.pt, 1) + " pt" : "—"}
        </td>
      </tr>
    );
  }

  return (
    <section>
      <h3 className="text-sm font-semibold mb-2">差異ランキング — {modeLabel(mode, derived)} <span className="text-[11px] font-normal text-slate-400">予算差額順 TOP7 (百万円)</span></h3>
      {/* [2026-09-11] 医業外損益 を 経費等 から外して独立行にしたので、 補助金の増減が
          「費用が減った＝良い」 に化けなくなった (26年07月 の 経費等 予算差 −47,280 千円 のうち
          −38,819＝82% は補助金等だった)。 区分バッジ「医業外」 で分けて見せる。 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-green-700 mb-0.5">😊 予算より良い方向 TOP 7</div>
          <table className="text-xs border w-full">
            <thead className="bg-green-50">
              <tr>
                <SortTh s={sGood} sortKey="metric" get={(r) => dispName((r as Row).metric)} className="px-2 py-1 border text-left">指標</SortTh>
                <SortTh s={sGood} sortKey="cat" get={(r) => (r as Row).cat} className="px-2 py-1 border">区分</SortTh>
                <SortTh s={sGood} sortKey="actual" get={(r) => (r as Row).actual} className="px-2 py-1 border text-right">実績</SortTh>
                <SortTh s={sGood} sortKey="budget" get={(r) => (r as Row).budget} className="px-2 py-1 border text-right">予算</SortTh>
                <SortTh s={sGood} sortKey="diff" get={(r) => (r as Row).diff} className="px-2 py-1 border text-right">差</SortTh>
                <SortTh s={sGood} sortKey="pt" get={(r) => (r as Row).pt ?? null} className="px-2 py-1 border text-right">収益比差</SortTh>
              </tr>
            </thead>
            <tbody>{sGood.rows.map((r) => <RankRow key={r.metric} r={r} good />)}</tbody>
          </table>
        </div>
        <div>
          <div className="text-xs font-semibold text-red-700 mb-0.5">😨 予算より悪い方向 TOP 7</div>
          <table className="text-xs border w-full">
            <thead className="bg-red-50">
              <tr>
                <SortTh s={sBad} sortKey="metric" get={(r) => dispName((r as Row).metric)} className="px-2 py-1 border text-left">指標</SortTh>
                <SortTh s={sBad} sortKey="cat" get={(r) => (r as Row).cat} className="px-2 py-1 border">区分</SortTh>
                <SortTh s={sBad} sortKey="actual" get={(r) => (r as Row).actual} className="px-2 py-1 border text-right">実績</SortTh>
                <SortTh s={sBad} sortKey="budget" get={(r) => (r as Row).budget} className="px-2 py-1 border text-right">予算</SortTh>
                <SortTh s={sBad} sortKey="diff" get={(r) => (r as Row).diff} className="px-2 py-1 border text-right">差</SortTh>
                <SortTh s={sBad} sortKey="pt" get={(r) => (r as Row).pt ?? null} className="px-2 py-1 border text-right">収益比差</SortTh>
              </tr>
            </thead>
            <tbody>{sBad.rows.map((r) => <RankRow key={r.metric} r={r} good={false} />)}</tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
